import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const wsRef = useRef(null);

  // Check token on mount: call getMe if token exists
  const fetchUser = useCallback(async () => {
    const savedToken = localStorage.getItem('token');
    if (!savedToken) {
      setLoading(false);
      return;
    }
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // WebSocket connection (带token认证)
  useEffect(() => {
    if (!user?.id) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const token = localStorage.getItem('token') || '';
    const wsUrl = `${protocol}//${host}/ws/notifications/${user.id}?token=${encodeURIComponent(token)}`;

    let reconnectCount = 0;
    let reconnectTimer = null;
    const MAX_RECONNECT = 10;

    const connect = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'pong') return;
          setNotifications((prev) => [
            { ...msg, id: Date.now(), timestamp: new Date().toISOString() },
            ...prev.slice(0, 49),
          ]);
        } catch { /* ignore */ }
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (reconnectCount < MAX_RECONNECT) {
          reconnectCount++;
          // 指数退避: 1s, 2s, 4s, 8s, 16s, 32s... 最大30s
          const delay = Math.min(1000 * Math.pow(2, reconnectCount - 1), 30000);
          reconnectTimer = setTimeout(connect, delay);
        }
      };

      ws.onopen = () => {
        reconnectCount = 0; // 连接成功后重置计数
      };

      ws.onerror = () => ws.close();
    };

    connect();

    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [user?.id]);

  // login: calls API, stores token in localStorage, sets user
  const login = async (credentials) => {
    const response = await authAPI.login(credentials);
    const data = response.data;
    const jwt = data.access_token;
    const userData = data.user;

    localStorage.setItem('token', jwt);
    setToken(jwt);
    setUser(userData);
    return userData;
  };

  // logout: clears token and user
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setNotifications([]);
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  // updateUser: update user info
  const updateUser = async (data) => {
    try {
      const response = await authAPI.updateMe(data);
      const updated = response.data?.data || response.data;
      setUser((prev) => ({ ...prev, ...updated }));
      return updated;
    } catch (err) {
      console.error('updateUser failed:', err);
      throw err;
    }
  };

  const dismissNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const isAuthenticated = !!user && !!token;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        updateUser,
        isAuthenticated,
        notifications,
        dismissNotification,
        clearNotifications,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export default AuthContext;
